import React from 'react';
import './PayInvoice.css';
import _ from 'lodash';
import { IInvoiceService } from '../../services/IInvoiceService';
import { Campaign, IAnalyticsService } from '../../services/IAnalyticsService';
import { HeaderFooterInvoice, HeaderInvoice } from '../components/HeaderInvoice';
import { shouldShowPayButton } from '../../utils/invoice';
import type { Location } from '@remix-run/router';
import { Consumer } from '../../enum/ConsumerEnum';
import { InitPaymentSelectorParams, IPaymentService } from '../../services/IPaymentService';
import { paymentErrorPage } from '../components/PaymentErrorPage';
import FooterInvoice from '../components/FooterInvoice';
import queryString from 'query-string';
import { buildCampaign } from '../../utils/CampaignBuilder';
import { EventName, PageName } from '../../enum/AnalyticsEnum';
import FSPlatformEndpointHelper from '../../utils/FSPlatformEndpointHelper';
import ViewPageState from '../viewInvoice/ViewState';
import { getHostnameForNz, shouldRedirectToNzBasedOnCountry } from '../../utils/redirectToDomain';

export interface PayInvoiceProps {
    readonly getInvoice: IInvoiceService['getInvoice'];
    readonly notifyEvent: IAnalyticsService['notifyEvent'];
    readonly initPaymentSelector: IPaymentService['initPaymentSelector'];
    readonly params?: any;
    readonly useLocation?: Location;
    readonly navigateToPage?: (link: string) => void;
    readonly getOIPDetails: IInvoiceService['getOIPDetails'];
}

export interface PayInvoiceState {
    readonly loading: boolean;
    readonly error: boolean;
    readonly inPayment: boolean;
    readonly payment_type: string;
    readonly hideAdminButtons: boolean;
    readonly invoice: any;
    readonly isPotentialFSClient?: boolean;
    readonly fsPlatformError?: boolean;
    readonly selector?: any;
}

export class PayInvoice extends React.Component<PayInvoiceProps, PayInvoiceState> {
    userActionHistory = [];

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: false,
            inPayment: true,
            payment_type: 'none',
            hideAdminButtons: true,
            invoice: null
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params != this.props.params) {
            this.setState({
                ...this.state,
                loading: true
            });
            const { invoiceId } = this.props.params;
            this.onGetInvoice(invoiceId);
        }
    }

    componentDidMount() {
        const { invoiceId } = this.props.params;
        this.onGetInvoice(invoiceId);
    }

    render() {
        const { fsPlatformError } = this.state;
        return (
            <div>
                <HeaderInvoice {...this.buildHeaderProps()} />
                {this.renderPaymentSelector()}
                {fsPlatformError && <FooterInvoice country={this.state?.invoice?.company?.country ?? ''} />}
                <HeaderFooterInvoice {...this.buildHeaderProps()} />
            </div>
        );
    }

    buildHeaderProps() {
        const { loading, error, invoice, hideAdminButtons, inPayment } = this.state;
        return { loading, error, invoice, hideAdminButtons, inPayment };
    }

    renderPaymentSelector() {
        const { loading, isPotentialFSClient, fsPlatformError, selector } = this.state;
        return (
            !loading && <div className="container">
                <div className="row">
                    <div className={'pay-ui' + (isPotentialFSClient ? ' fs-payment-container' : '')}>
                        {
                            isPotentialFSClient &&
                            <div>
                                {
                                    fsPlatformError ?
                                        paymentErrorPage({
                                            cancelPayment: this.cancelPayment,
                                            isSinglePaymentError: true,
                                            errorType: 'errorInitFsPaymentSelector',
                                            errorTracking: this.errorTracking
                                        }) :
                                        <div>
                                            <div id="close-payment-modal" data-testid="close-payment-modal"
                                                 onClick={() => {
                                                     this.cancelPayment();
                                                     this.notifyCloseEvent();
                                                 }}
                                                 className="cancel-pay icon-remove fs-cancel-button" />
                                            <h2 className="text-center">Payment method</h2>
                                            <iframe src={selector.selectorURL} id="paymentOptions"
                                                    className="fs-payment-options" scrolling="no" allow="payment" />
                                        </div>
                                }
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    async onGetInvoice(invoiceId) {
        const { getInvoice, useLocation, initPaymentSelector } = this.props;
        getInvoice(invoiceId, undefined).then(async res => {
            const invoice = res.data;
            const company = invoice.company;

            const currentUrl = new URL(location.href);
            if (shouldRedirectToNzBasedOnCountry(currentUrl, company.country)) {
                location.replace(getHostnameForNz(currentUrl).toString());
                return;
            }

            const cdfGuid = company.uid;
            const { clientId, isPayable } = await this._getOIPInfo(company, cdfGuid, invoice);
            if (!isPayable) {
                ViewPageState.noteVisible = true;
                return this.cancelPayment();
            }

            const businessId = invoice.consumer === Consumer.ARL ? company.uid : company.serial_number;
            const search = _.get(useLocation, 'search', '');
            const query = queryString.parse(search);
            const source = _.get<any, any, string>(query, 'source', '');
            const meta: any = invoice.meta;
            const enabledPaymentOptions = invoice.payment_options.map(paymentOption => paymentOption.type);
            const invoiceLineItems = meta.line_items.map(line => ({
                description: line.Description,
                amount: line.Total,
                invoiceId: meta.id,
                taxInfo: line.taxInfo
            }));
            if (_.includes(enabledPaymentOptions, 'creditCard')) {
                enabledPaymentOptions.push('applePay');
                enabledPaymentOptions.push('googlePay');
            }
            const initPaymentSelectorParams: InitPaymentSelectorParams = {
                isBulk: false,
                clientId,
                clientReportRef: meta.id,
                clientRef: meta.number,
                crn: invoice.paymentDetails?.bpay?.reference_number || '',
                businessId,
                currency: meta.currency,
                totalAmount: meta.due_amount,
                invoiceHash: invoiceId,
                source,
                surchargeEnabled: meta.surcharge_enabled,
                invoiceLineItems,
                enabledPaymentOptions,
                customerId: invoice.customer.id,
                country: company.country,
                invoices: [{
                    surchargingEnabled: meta.surcharge_enabled,
                    invoiceId: meta.id,
                    freight: meta.freight,
                    freightTaxCode: meta.freight_tax_code,
                    currency: meta.currency,
                    paymentAmount: meta.due_amount,
                    paymentCentAmount: meta.due_amount * 100,
                    discountAmount: meta.discount_amount
                }]
            };
            initPaymentSelector(initPaymentSelectorParams).then(fsPlatformInfo => {
                if (fsPlatformInfo) {
                    this.setState((state) => ({
                        ...state,
                        isPotentialFSClient: fsPlatformInfo.isPotentialFSClient,
                        fsPlatformError: !fsPlatformInfo.isPotentialFSClient
                    }));
                    if (!this.state.fsPlatformError) {
                        FSPlatformEndpointHelper.appendScriptFromPlatformEndpoint(fsPlatformInfo);
                        this.setState({
                            ...this.state,
                            selector: fsPlatformInfo.selector,
                            invoice: invoice,
                            loading: false
                        }, () => this.loadSegmentEvent());
                    }
                }
            }).catch(() => {
                this.setState({
                    ...this.state,
                    isPotentialFSClient: true,
                    fsPlatformError: true,
                    invoice: invoice,
                    loading: false
                }, () => this.loadSegmentEvent());
            });
        }).catch(() => {
            this.setState({
                ...this.state,
                loading: false,
                error: true
            });
        });
    }

    private async _getOIPInfo(company, cdfGuid, invoice) {
        let clientId: string;
        let isPayable;
        clientId = '';
        if (company.country == 'NZ') {
            try {
                const res = await this.props.getOIPDetails(cdfGuid);
                clientId = res.clientId;
                isPayable = shouldShowPayButton(invoice, res.OIPEnabled);
            } catch (e) {
                isPayable = false;
            }
        } else {
            isPayable = shouldShowPayButton(invoice);
            clientId = this.getClientIdFromCreditCardFirstOption(invoice);
        }
        return { clientId, isPayable };
    }

    getClientIdFromCreditCardFirstOption(invoice) {
        const creditCardOptions = (invoice.payment_options || []).filter(option => option.type === 'creditCard');
        if (creditCardOptions.length < 1) {
            return null;
        }
        return creditCardOptions[0].client_id;
    }

    cancelPayment = () => {
        const { navigateToPage, useLocation, params } = this.props;
        navigateToPage && navigateToPage(`/${params.invoiceId}${useLocation?.search ? useLocation.search : ''}`);
    };

    notifyCloseEvent = () => {
        const { invoice } = this.state;
        const { useLocation, notifyEvent } = this.props;
        const search = _.get(useLocation, 'search', '');
        const query = queryString.parse(search);
        const source = _.get(query, 'source', '');
        const type = _.get(query, 'type', '');
        const campaign: Campaign = buildCampaign(PageName.PAY_VIEW, type);
        const activityData = {
            invoice: invoice,
            source,
            campaign,
            invoiceId: _.get(query, 'invoiceId', '')
        };
        notifyEvent(EventName.CLOSE_PAY_BUTTON, activityData);
    };

    loadSegmentEvent() {
        const { useLocation, notifyEvent } = this.props;
        const { invoice } = this.state;
        const search = useLocation && useLocation.search ? useLocation.search : '';
        const query = queryString.parse(search);
        const source = _.get(query, 'source', '');
        const type = _.get(query, 'type', '');
        const invoiceId = _.get(query, 'invoiceId', '');
        const campaign: Campaign = buildCampaign(PageName.PAY_VIEW, type);
        const activityData = {
            invoice: invoice,
            source,
            campaign,
            invoiceId
        };
        notifyEvent(EventName.PAY_VIEW_INVOICE, activityData);
    }

    errorTracking = (errorType: string) => {
        const { invoice } = this.state;
        const { useLocation, notifyEvent } = this.props;
        const userId = invoice.consumer + '/' + invoice.company.uid;
        const search = useLocation && useLocation.search ? useLocation.search : '';
        const query = queryString.parse(search);
        const source = _.get(query, 'source', '');
        const activityData = {
            userId,
            companyId: invoice.company.uid,
            consumer: invoice.consumer,
            source,
            customerId: invoice.customer.id,
            invoiceListCount: 1,
            history: this.userActionHistory,
            errorType
        };
        notifyEvent(EventName.INVOICE_LIST_ERROR, activityData);
    };
}

export default PayInvoice;
